import { TFunction } from 'i18next';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { HintProps } from '@form/components/FormHints/FormHints.component';
import { formatNumberWithComma } from '@utils/number';
import { formatSize } from '@utils/strings';

export interface Validator {
  isValid: boolean;
  type?: 'error' | 'success';
  message: string;
}

export interface Value {
  label?: string;
  value: string;
}

export interface ValueWithSelection extends Value {
  selected: boolean;
}

export type FormElement = {
  variant?: 'full-width' | 'small-input' | 'very-small-input';
};

export const formRegex = {
  digit: /^[0-9]*$/,
  email: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
  password: /(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/g,
  phone: /^(\+)?[0-9]+$/,
  username: /^[\w.]{3,30}$/,
  zipcode: /\b\d{5}\b/,
};

export const formErrorsMsgs = {
  emailInvalid: 'common.form.email-invalid',
  emptyEmail: 'auth.email.required',
  emptyPassword: 'auth.password.required',
  emptyUsername: 'profile.form.username.error.empty',
  fieldEmpty: 'common.form.empty-field',
  fieldNotValid: 'common.form.not-valid-field',
  fileSize: 'common.form.not-valid-file-size',
  needToCheck: 'auth.checkbox.need-to-check',
  usernameInvalid: 'common.form.username-invalid',
  valueBetween: 'common.form.value-between.with-unit',
  zipcodeInvalid: 'common.form.zipcode.error',
};

interface FormValidator {
  digit: () => Validator;
  email: () => Validator;
  zipcode: () => Validator;
  password: () => Validator;
  phone: () => Validator;
  required: () => Validator;
  username: () => Validator;
  fileSize: (size: number, type?: string) => Validator;
  valueBetween: (min: number, max: number, unity: string) => Validator;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formValidator = (value: any, translateFn: TFunction, requiredField: boolean): FormValidator => {
  const isEmptyValue = ['number', 'boolean'].includes(typeof value) ? false : isEmpty(value);
  const defaultValidity: Validator =
    requiredField && isEmptyValue
      ? { isValid: false, message: translateFn(formErrorsMsgs.fieldEmpty) }
      : {
          isValid: true,
          message: '',
        };

  function testRegex(regex: RegExp, i18nErrorMessage: string = formErrorsMsgs.fieldNotValid) {
    if (defaultValidity.isValid && !isEmptyValue && !regex.test(value)) {
      return { isValid: false, message: translateFn(i18nErrorMessage) };
    }
    return defaultValidity;
  }

  function fileSize(size: number, type = 'photo') {
    if (defaultValidity.isValid && value && value instanceof File) {
      return {
        isValid: value.size < size,
        message: translateFn(formErrorsMsgs.fileSize, { size: formatSize(size), type }),
      };
    }
    return defaultValidity;
  }

  function valueBetween(min: number, max: number, unityI18n: string) {
    if (!defaultValidity.isValid || isEmptyValue || (typeof value === 'number' && value >= min && value <= max)) {
      return defaultValidity;
    }
    return {
      isValid: false,
      message: translateFn(formErrorsMsgs.valueBetween, {
        max: formatNumberWithComma(max, 1),
        min: formatNumberWithComma(min, 1),
        unity: translateFn(unityI18n),
      }),
    };
  }

  return {
    digit: () => testRegex(formRegex.digit),
    email: () => testRegex(formRegex.email, formErrorsMsgs.emailInvalid),
    fileSize,
    password: () => testRegex(formRegex.password),
    phone: () => testRegex(formRegex.phone),
    required: () => defaultValidity,
    username: () => testRegex(formRegex.username, formErrorsMsgs.usernameInvalid),
    valueBetween,
    zipcode: () => testRegex(formRegex.zipcode, formErrorsMsgs.zipcodeInvalid),
  };
};

export type CheckboxProps = HintProps & {
  label: ReactNode;
  checked?: boolean;
  onCheck: (checked: boolean) => void;
  variant?: 'checkbox' | 'radio';
  disabled?: boolean;
  multilineLabel?: boolean;
  isDarkGreen?: boolean;
};
